import { useState } from 'react'
import {
  UserPlus,
  MessageSquare,
  FileText,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/empty-state'
import { cn } from '@/lib/utils'
import { useDashboardActivity, type ActivityType } from './use-dashboard'

const COLLAPSED_LIMIT = 6
const EXPANDED_LIMIT = 50

const ACTIVITY_ICON_MAP: Record<ActivityType, { icon: LucideIcon; color: string }> = {
  connection: { icon: UserPlus, color: 'text-blue-500 bg-blue-500/10' },
  reply: { icon: MessageSquare, color: 'text-emerald-500 bg-emerald-500/10' },
  post: { icon: FileText, color: 'text-purple-500 bg-purple-500/10' },
  enrichment: { icon: Sparkles, color: 'text-amber-500 bg-amber-500/10' },
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now()
  const then = new Date(timestamp).getTime()
  const diffMs = now - then

  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`

  return new Date(timestamp).toLocaleDateString()
}

function ActivityItemSkeleton() {
  return (
    <div className="flex items-start gap-3 py-3">
      <Skeleton className="h-8 w-8 rounded-full shrink-0" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-full max-w-[280px]" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  )
}

export function ActivityFeed() {
  const [expanded, setExpanded] = useState(false)
  const { data: activity, isLoading } = useDashboardActivity(expanded ? EXPANDED_LIMIT : COLLAPSED_LIMIT)

  // Hide the toggle when there's nothing to expand into
  const hasMoreToShow = !expanded && (activity?.length ?? 0) >= COLLAPSED_LIMIT
  const showToggle = expanded || hasMoreToShow

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Recent Activity</CardTitle>
        {showToggle && (
          <button
            className="text-sm text-cobalt-bright hover:underline"
            type="button"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Show less' : 'View all'}
          </button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="divide-y">
            {Array.from({ length: 4 }).map((_, i) => (
              <ActivityItemSkeleton key={i} />
            ))}
          </div>
        ) : !activity || activity.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No activity yet"
            description="Activity will appear here as your automation runs. Connect your LinkedIn account and activate to get started."
          />
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="divide-y">
              {activity.map((item) => {
                const iconConfig = ACTIVITY_ICON_MAP[item.type]
                const Icon = iconConfig.icon
                const colorClasses = iconConfig.color.split(' ')

                return (
                  <div key={item.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                    <div
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                        colorClasses[1],
                      )}
                    >
                      <Icon className={cn('h-4 w-4', colorClasses[0])} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-snug">{item.description}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatRelativeTime(item.timestamp)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  )
}
